import { FormProvider, useForm } from "react-hook-form";
import s from "./settings-form.module.scss";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@shared/ui/button/button";
import { Alert } from "@shared/ui/alert/alert";
import { useProfile } from "@shared/api/useProfile";
import { useUpdateProfile } from "@entities/profile/api/useUpdateProfile";
import { useUpdateEmail } from "@entities/profile/api/useUpdateEmail";
import { useUpdateGrade } from "@entities/profile/api/useUpdateGrade";
import { useUpdatePhotos } from "@widgets/settings/api/useUpdatePhotos";
import { useUpdateVideoPresentation } from "@entities/profile/api/useUpdateVideoPresentation";
import { UpdateAvatarModal } from "@features/profile/update-avatar";
import { VacationButton } from "@features/profile/vacation";
import { SignOfferModal } from "@features/profile/sign-offer";
import { AvatarSetting } from "@widgets/settings/ui/avatar-setting/avatar-setting";
import { PhotosSetting } from "@widgets/settings/ui/photos-setting/photos-setting";
import { FullNameSetting } from "@widgets/settings/ui/full-name-setting/full-name-setting";
import { InstrumentsSetting } from "@widgets/settings/ui/instruments-setting/instruments-setting";
import { GradeSetting } from "@widgets/settings/ui/grade-setting/grade-setting";
import { EducationSetting } from "@widgets/settings/ui/education-setting/education-setting";
import { EmailSetting } from "@widgets/settings/ui/email-setting/email-setting";
import { PhoneSetting } from "@widgets/settings/ui/phone-setting/phone-setting";
import { TeacherBioSetting } from "@widgets/settings/ui/teacher-bio-setting/teacher-bio-setting";
import { VideoRow } from "@widgets/settings/ui/video-row/video-row";
import { SettingsNav } from "@widgets/settings/ui/settings-nav/settings-nav";
import { DiscountSetting } from "@widgets/settings/ui/discount-setting/discount-setting";
import { ContractFile } from "@widgets/settings/ui/contract-file/contract-file";
import { PaymentCard } from "@widgets/settings/ui/payment-card/payment-card";

export const SettingsForm = ({ isBottom }) => {
	const { data: profile } = useProfile();
	const location = useLocation();

	const { mutate: updateProfile, isPending } = useUpdateProfile();
	const { mutate: updateGrade } = useUpdateGrade();
	const { mutate: updateEmail } = useUpdateEmail();
	const { mutate: updatePhotos, isPending: photosIsPending } = useUpdatePhotos();
	const { mutate: updateVideo } = useUpdateVideoPresentation();

	const [localVideo, setLocalVideo] = useState(null);
	const [myFiles, setMyFiles] = useState([]);
	const [globalFiles, setGlobalFiles] = useState(profile?.education_proof || []);
	const [myPhotos, setMyPhotos] = useState([]);
	const [globalPhotos, setGlobalPhotos] = useState(profile?.photos || []);
	const [isSignOfferModalOpen, set_isSignOfferModalOpen] = useState(false);
	const [activeSection, setActiveSection] = useState("profile");

	const profileRef = useRef(null);
	const cardRef = useRef(null);
	const offerRef = useRef(null);

	const sectionLinks = [
		{ id: "profile", label: "Профиль", ref: profileRef },
		...(profile?.role === "teacher"
			? [
					{ id: "offer", label: "Договор", ref: offerRef },
					{ id: "card", label: "Финансы", ref: cardRef },
				]
			: []),
	];

	const scrollToSection = (section) => {
		section.ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
		setActiveSection(section.id);
	};

	// Скролл к секции при переходе из чек-листа (например, "Подписать договор")
	useEffect(() => {
		const sectionId = location.state?.section;
		if (!sectionId) return;

		const section = sectionLinks.find((link) => link.id === sectionId);
		if (section) {
			setTimeout(() => scrollToSection(section), 100);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setGlobalFiles(profile?.education_proof || []);
		setGlobalPhotos(profile?.photos || []);
	}, [profile]);

	const methods = useForm({
		defaultValues: {
			...profile,
			instruments: profile?.instruments?.length > 0
				? profile?.instruments?.map((el) => ({
						value: el.id,
						label: el.name,
					}))
				: [],
			fname: profile?.fname || "",
			lname: profile?.lname || "",
		},
	});
	const { formState } = methods;

	const checkUpdatedValue = (valueName) => {
		if (!valueName) return;

		const newValue = ((typeof methods.watch(valueName) === "string"
			? methods.watch(valueName).trim()
			: methods.watch(valueName))) || "";
		const oldValue = profile?.[valueName] || "";

		return !!(newValue !== oldValue);
	};

	const checkUpdated = (array) => {
		for (let index = 0; index < array.length; index++) {
			if (array[index] && checkUpdatedValue(array[index])) {
				return "updated";
			}
		}
	};

	const info_verified = profile?.info_verified || (!profile?.info_verified && (!profile?.teacher_bio || !profile?.education_proof));

	const newValues = () => {
		const watchedInstruments = methods?.watch("instruments");
		const local_instruments = watchedInstruments?.length > 0
			? JSON.stringify(
					watchedInstruments
						.map((instrument) => instrument.value)
						.sort((a, b) => a - b)
				)
			: null;
		const oldInstruments = profile?.instruments?.length > 0
			? JSON.stringify(
					profile?.instruments
						?.map((instrument) => instrument.id)
						.sort((a, b) => a - b) || ""
				)
			: null;
		const oldEducation = profile?.education_proof?.length > 0 ? JSON.stringify(profile?.education_proof) : null;
		const newEducation = globalFiles?.length > 0 ? JSON.stringify(globalFiles) : null;

		const checkFields = info_verified
			? ["name", "email", "grade_id", "fname", "lname", "teacher_bio"]
			: ["name", "email", "grade_id", "fname", "lname"];

		return checkUpdated(checkFields)
			|| local_instruments !== oldInstruments
			|| (newEducation !== oldEducation || myFiles?.length > 0);
	};

	const handleSubmit = methods.handleSubmit((data) => {
		const hasNewEducationFiles = myFiles?.length > 0;

		if (newValues() || hasNewEducationFiles) {
			const payload = {
				name: data?.name,
				fname: data?.fname ? data?.fname : null,
				lname: data?.lname ? data?.lname : null,
				instruments: data?.instruments
					? data?.instruments?.map((instrument) => instrument.value)
					: null,
				teacher_bio: data?.teacher_bio ? data?.teacher_bio : null,
				attachments: myFiles,
				education_proof: globalFiles?.length ? globalFiles?.map((el) => el?.id) : [],
			};

			updateProfile(payload);
		}

		if (localVideo) {
			updateVideo(localVideo);
		}

		if (myPhotos?.length > 0 || globalPhotos?.length !== profile?.photos?.length) {
			updatePhotos({ attachments: myPhotos, photos: globalPhotos?.length ? globalPhotos?.map((el) => el?.id) : [] });
		}

		if (data?.grade_id && data?.grade_id !== profile?.grade_id) {
			updateGrade({ grade_id: data?.grade_id });
		}

		if (data?.email && data?.email !== profile?.email) {
			updateEmail({ email: data?.email });
		}

		setMyFiles([]);
		setMyPhotos([]);
		setLocalVideo(null);
	});

	const btnDisabled = newValues()
		? ((!formState.isValid && formState.isDirty) || isPending)
		: (((globalPhotos?.length || 0) === (profile?.photos?.length || 0) && myPhotos?.length <= 0) && !localVideo);

	const handleReset = () => {
		methods.reset();
		setMyFiles([]);
		setGlobalFiles(profile?.education_proof || []);
		setMyPhotos([]);
		setGlobalPhotos(profile?.photos || []);
		setLocalVideo(null);
	};

	return (
		<FormProvider {...methods}>
			<UpdateAvatarModal />
			<form
				className={s.form}
				onSubmit={(e) => {
					e.preventDefault();
				}}
			>
				<div className={s.title}>Профиль</div>
				{profile?.role === "teacher" && (
					<SettingsNav
						activeSection={activeSection}
						links={sectionLinks}
						onNavigate={scrollToSection}
					/>
				)}

				<section ref={profileRef} id="settings-section-profile" className={s.section}>
					<div className={s.card}>
						<AvatarSetting />
						{profile?.role === "teacher" && (
							<PhotosSetting
								myFiles={myPhotos}
								setMyFiles={setMyPhotos}
								globalFiles={globalPhotos}
								setGlobalFiles={setGlobalPhotos}
								photosIsPending={photosIsPending}
							/>
						)}
						{profile?.role === "teacher" ? <VideoRow {...{ setLocalVideo, localVideo }} /> : ""}
						<FullNameSetting requireFname={profile?.role === "teacher"} />
						{profile?.role === "teacher" ? (
							<>
								<InstrumentsSetting methods={methods} />
								<GradeSetting methods={methods} />
								{info_verified ? "" : (
									<Alert
										className={s.alert}
										title="Данные на модерации"
										subtitle="Модераторы скоро проверят их. По завершении вам придет уведомление "
									/>
								)}
								<TeacherBioSetting {...{ info_verified, methods }} />
								<EducationSetting {...{ setMyFiles, setGlobalFiles, myFiles, globalFiles, info_verified }} />
							</>
						) : (
							""
						)}
						<EmailSetting />
						<PhoneSetting />
					</div>
				</section>

				{profile?.role === "teacher" && (
					<>
						<section ref={cardRef} id="settings-section-card" className={s.section}>
							<div className={s.card}>
								<div className={s.cardHeader}>
									<div className={s.descLabel}>Ваша карта</div>
								</div>
								<PaymentCard profile={profile} />
							</div>
						</section>

						<section id="settings-section-discounts" className={s.section}>
							<div className={s.card}>
								<div className={s.cardHeader}>
									<div className={s.descLabel}>Акции для учеников</div>
								</div>
								<DiscountSetting profile={profile} />
							</div>
						</section>

						<section id="settings-section-vacation" className={s.section}>
						<div className={s.card}>
							<div className={s.cardHeader}>
								<div className={s.descLabel}>Отпуск</div>
							</div>
							<VacationButton />
						</div>
					</section>

					<section ref={offerRef} id="settings-section-offer" className={s.section}>
							<div className={s.card}>
								<div className={s.cardHeader}>
									<div className={s.descLabel}>Договор-оферта</div>
								</div>
								<ContractFile />
								{profile?.signed_docs ? (
									<span className={s.offerStatus}>Подписано</span>
								) : (
									<>
										<span className={s.inputSubtitle}>
											Для работы с платными занятиями необходимо подписать договор-оферту.
										</span>
										<Button
											label="Подписать договор"
											onClick={() => set_isSignOfferModalOpen(true)}
											size="small"
										/>
									</>
								)}
							</div>
						</section>

						<SignOfferModal
							isOpen={isSignOfferModalOpen}
							close={() => set_isSignOfferModalOpen(false)}
							profile={profile}
						/>
					</>
				)}

				<div className={classNames(s.buttonsWrapper, isBottom ? s.isBottom : "")}>
					<div className={s.buttonsTitle}>Сохранить изменения</div>
					<div className={s.btns}>
						<Button
							label="Отменить"
							size="small"
							theme="secondary"
							className={s.button}
							onClick={handleReset}
							disabled={btnDisabled}
						/>
						<Button
							label="Сохранить"
							size="small"
							className={s.button}
							disabled={btnDisabled}
							fetching={isPending}
							onClick={() => handleSubmit()}
						/>
					</div>
				</div>
			</form>
		</FormProvider>
	);
};
